// Görevler sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'tasks.list';

export const tasksListGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Görevler sayfası nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Görev takip sistemi, işletmenizin günlük operasyonlarını ve personel görevlerini yönetmenizi sağlar. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık'),
    stats: t(`${G}.regions.stats`, 'Görev tipi sekmeleri'),
    filters: t(`${G}.regions.filters`, 'Arama ve filtreler'),
    toolbar: t(`${G}.regions.toolbar`, 'Görünüm ve işlem düğmeleri'),
    table: t(`${G}.regions.table`, 'Görev kartları / tablosu'),
    pagination: t(`${G}.regions.pagination`, 'Sayfalama'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Görevler sistemi nasıl çalışır?'),
      text: t(`${G}.s1.text`, 'Tekrarlanan görevleri ve bir kerelik görevleri planlar, personele atar ve takip edersiniz. Şube yöneticisi olarak girdiyseniz başlık "Şube Görevleri" olur ve yalnız şubenizin görevlerini görürsünüz.'),
    },
    {
      region: 'stats',
      title: t(`${G}.s2.title`, '2. Görev tipini seçin'),
      text: t(`${G}.s2.text`, 'Üstteki sekmelerden görev tipini seçin: Normal Görevler, Ekip Görevleri, Günlük Tekrarlı, Haftalık Tekrarlı, Aylık Tekrarlı, Yıllık Görevler ve Takvim.'),
      tips: [
        t(`${G}.s2.tip1`, 'Tekrarlı görevler belirli saatlerde tekrarlanır; her biri için tolerans süresi belirleyebilirsiniz.'),
        t(`${G}.s2.tip2`, 'Takvim sekmesi yalnız şirket yöneticisine görünür.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s3.title`, '3. Görev ve grup ekleyin'),
      text: t(`${G}.s3.text`, '"Yeni Görev Ekle" ile görev oluşturup bir personele atarsınız; tekrarlı olarak yapılması gereken görevleri de buradan tanımlarsınız. "Grup Ekle" ile görevleri kategorize eden gruplar oluşturur, benzer görevleri bir arada yönetirsiniz.'),
      tips: [
        t(`${G}.s3.tip1`, '"Excel İndir" açık sekmedeki görevleri Excel dosyası olarak indirir.'),
        t(`${G}.s3.tip2`, 'Kart / liste düğmeleriyle görünümü değiştirirsiniz (ayrıntı 7. adımda).'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s4.title`, '4. Görevleri süzün'),
      text: t(`${G}.s4.text`, 'Durum, Personel, Grup ve Şube filtreleriyle listeyi daraltın; arama kutusuna görev adını yazın.'),
      tips: [
        t(`${G}.s4.tip1`, 'Durum filtresi: Tüm Görevler, Bekleyen, Kabul Edilen, Başlandı, Tamamlanan, Kaçırılan, Reddedilen.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Zaman renk kodlarını okuyun'),
      text: t(`${G}.s5.text`, 'Görev zamanları durumlarına göre farklı renklerle gösterilir.'),
      tips: [
        t(`${G}.s5.tip1`, 'Gri – Zamanı Gelmemiş: henüz zamanı gelmemiş görevler.'),
        t(`${G}.s5.tip2`, 'Mavi – Yaklaşıyor: zamanı yaklaşan görevler.'),
        t(`${G}.s5.tip3`, 'Mavi – Aktif: şu an aktif olan görevler.'),
        t(`${G}.s5.tip4`, 'Kırmızı – Kaçırılmış: zamanında yapılmamış görevler.'),
        t(`${G}.s5.tip5`, 'Yeşil – Tamamlanmış: tamamlanan görevler.'),
        t(`${G}.s5.tip6`, 'Kahverengi – Başlatılmış: personelin başlattığı görevler.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s6.title`, '6. Görev zamanı nasıl belirlenir?'),
      text: t(`${G}.s6.text`, 'Sistem her görev zamanının durumunu şu kurallarla belirler.'),
      tips: [
        t(`${G}.s6.tip1`, 'Yaklaşıyor: görev, başlama saatinden önceki tolerans süresinin içindeyse (ör. 15 dk) "Yaklaşıyor" olur.'),
        t(`${G}.s6.tip2`, 'Aktif: görev saati geldiğinde başlar ve bir sonraki görevin tolerans başlangıcına kadar "Aktif" kabul edilir.'),
        t(`${G}.s6.tip3`, 'Kaçırılmış: aktif olma süresi geçtiği hâlde tamamlanmayan görev "Kaçırılmış" olarak işaretlenir.'),
        t(`${G}.s6.tip4`, 'Önemli: son görev için 60 dakikalık tolerans uygulanır. Diğer görevlerde bir sonraki görevin tolerans başlangıcı, mevcut görevin bitiş zamanı sayılır.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s7.title`, '7. Kart ve tablo görünümü'),
      text: t(`${G}.s7.text`, 'Kart görünümünde her görevin ayrıntıları görünür: görev adı, açıklama, atanan personel ve tekrarlama zamanları. Tablo görünümü daha kompakttır: durum ikonu, görev adı, zamanlar, tolerans süresi, personel bilgisi ve işlem düğmeleri. Tablo uzunsa alttaki sayfalama ile ilerleyin.'),
      tips: [
        t(`${G}.s7.tip1`, 'Bir göreve tıklayınca ayrıntıları açılır; "QR Yazdır" ile görevin QR kodunu yazdırırsınız.'),
        t(`${G}.s7.tip2`, 'Personelin reddettiği bir görevi "Görevi Yenile" ile, personele bir not yazarak yeniden gönderebilirsiniz.'),
        t(`${G}.s7.tip3`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
